import { useAuth0 } from '@auth0/auth0-react';
import { Button, ButtonProps, HStack, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { useUserContext } from 'context/UserContextProvider';
import { useTranslation } from 'react-i18next';
import { LuLogOut } from 'react-icons/lu';

import config from '../config';
import { fontWeights } from '../global/Fonts';

type LogoutButtonProps = ButtonProps & { showIcon?: boolean; isCollapsed?: boolean; isSidebar?: boolean };

const LogoutButton = ({ showIcon = true, isCollapsed = false, isSidebar = false, ...props }: LogoutButtonProps) => {
  const { logout } = useAuth0();
  const { t } = useTranslation();
  const { logout: contextLogout } = useUserContext();

  const onClick = () => {
    contextLogout?.();
    logout({ returnTo: config.baseUrl.toString() });
  };

  if (isCollapsed) {
    return (
      <Tooltip label={t('common:logout')} placement="right" hasArrow openDelay={200}>
        <IconButton
          variant="ghost-on-accent"
          w="full"
          h="3rem"
          minW="2.5rem"
          paddingInline={0}
          borderRadius="md"
          color="white"
          transition="none"
          _hover={{ bg: 'whiteAlpha.400' }}
          aria-label={t('common:logout')}
          icon={<Icon as={LuLogOut} boxSize="6" />}
          {...props}
          onClick={onClick}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      variant="ghost-on-accent"
      justifyContent="start"
      data-test-id="logout-button"
      fontFamily="body"
      fontWeight={fontWeights.normal}
      paddingInline={0}
      // In the sidebar it has to read as one of the nav rows, not as a themed accent button.
      {...(isSidebar && {
        w: 'full',
        h: '3rem',
        px: 3,
        borderRadius: 'md',
        color: 'white',
        transition: 'none',
        _hover: { bg: 'whiteAlpha.400' },
      })}
      {...props}
      onClick={onClick}
    >
      <HStack spacing="3">
        {showIcon && <Icon as={LuLogOut} boxSize="6" />}
        <Text>{t('common:logout')}</Text>
      </HStack>
    </Button>
  );
};

export default LogoutButton;
